import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';

const ParrotCreation = ({ 
  onComplete, 
  onCancel, 
  initialName = '', 
  initialPrompt = '',
  showNameInput = true,
  showVoiceSelection = true,
  mode = 'standalone', // 'standalone' or 'onboarding'
  // Enhanced props for better integration
  selectedPhoneNumber = null,
  selectedPhoneNumbers = [],
  onStepChange = null,
  enableProgressSaving = false,
  customSteps = null,
  theme = 'light', // 'light' or 'dark'
  // Enhanced onboarding props
  onNext,
  onBack,
  data,
  setData,
  onError,
  isOnboarding = false
}) => {
  const { getToken } = useAuth();
  
  // Form state
  const [parrotName, setParrotName] = useState(initialName);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [playingPreview, setPlayingPreview] = useState(null);
  
  // Chat state
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  
  // UI state
  const [currentStep, setCurrentStep] = useState(showNameInput ? 1 : (showVoiceSelection ? 2 : 3));
  const [isLoading, setIsLoading] = useState(false);
  
  // Enhanced state management
  const [errors, setErrors] = useState({});
  const [progressData, setProgressData] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const [lastSavedStep, setLastSavedStep] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Enhanced steps configuration with custom step support
  const defaultSteps = [
    ...(showNameInput ? [{ id: 1, title: 'Name Your Parrot', description: 'Give your AI assistant a name', icon: 'user' }] : []),
    ...(showVoiceSelection ? [{ id: showNameInput ? 2 : 1, title: 'Choose Voice', description: 'Select the perfect voice', icon: 'microphone' }] : []),
    { id: showNameInput && showVoiceSelection ? 3 : (showNameInput || showVoiceSelection ? 2 : 1), title: 'Configure Personality', description: 'Chat to set up behavior', icon: 'chat' }
  ];
  
  const steps = customSteps || defaultSteps;

  // Load voices on mount
  useEffect(() => {
    fetchVoices();
  }, []);

  // Auto-scroll chat messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load saved progress on mount
  useEffect(() => {
    const loadSavedProgress = async () => {
      const savedProgress = await loadProgress();
      if (savedProgress && savedProgress.step) {
        // Restore saved state
        if (savedProgress.parrot_name) setParrotName(savedProgress.parrot_name);
        if (savedProgress.selected_voice) setSelectedVoice(savedProgress.selected_voice);
        if (savedProgress.temperature !== undefined) setTemperature(savedProgress.temperature);
        if (savedProgress.conversation_id) setConversationId(savedProgress.conversation_id);
        if (savedProgress.messages) setMessages(savedProgress.messages);
        
        // Set the last saved step indicator
        const stepMatch = savedProgress.step.match(/step_(\d+)_completed/);
        if (stepMatch) {
          setLastSavedStep(parseInt(stepMatch[1]));
        }
      }
    };
    
    if (enableProgressSaving) {
      loadSavedProgress();
    }
  }, [enableProgressSaving]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchVoices = async () => {
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/voices', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const voicesData = await response.json();
        setVoices(voicesData);
        // Set first voice as default if none selected
        if (voicesData.length > 0 && !selectedVoice) {
          setSelectedVoice(voicesData[0].service_voice_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    }
  };

  const startChatConversation = async (retryAttempt = 0) => {
    setIsStartingChat(true);
    setErrors(prev => ({ ...prev, chat: null }));
    
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/parrots/chat/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_type: 'parrot_setup',
          // Enhanced context for better AI responses
          context: {
            parrot_name: parrotName,
            selected_voice: selectedVoice,
            mode: mode,
            phone_number: selectedPhoneNumber?.friendlyName || null
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setConversationId(data.conversation_id);
        setMessages([{
          role: 'assistant',
          content: data.message,
          timestamp: new Date()
        }]);
        setRetryCount(0); // Reset retry count on success
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      
      // Enhanced retry logic
      if (retryAttempt < 3) {
        setTimeout(() => {
          startChatConversation(retryAttempt + 1);
        }, 1000 * (retryAttempt + 1)); // Exponential backoff
      } else {
        setErrors(prev => ({ 
          ...prev, 
          chat: 'Unable to start conversation. Please check your connection and try again.' 
        }));
      }
    } finally {
      setIsStartingChat(false);
    }
  };

  const sendChatMessage = async (retryAttempt = 0) => {
    if (!currentMessage.trim() || !conversationId || isChatLoading) return;

    const userMessage = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = currentMessage;
    setCurrentMessage('');
    setIsChatLoading(true);
    setErrors(prev => ({ ...prev, message: null }));

    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/parrots/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: messageToSend,
          // Enhanced context preservation
          context: {
            current_step: currentStep,
            parrot_name: parrotName,
            selected_voice: selectedVoice
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          role: 'assistant',
          content: data.message,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Enhanced completion detection with multiple triggers
        const completionTriggers = [
          'ready', 'complete', 'finalize', 'finished', 'done',
          'create parrot', 'setup complete', 'configuration complete'
        ];
        
        const messageContent = data.message.toLowerCase();
        const userMessageContent = messageToSend.toLowerCase();
        
        if (completionTriggers.some(trigger => 
          messageContent.includes(trigger) || userMessageContent.includes(trigger)
        )) {
          setShowCompletion(true);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Enhanced retry logic for message sending
      if (retryAttempt < 2) {
        setTimeout(() => {
          // Restore the message and retry
          setCurrentMessage(messageToSend);
          setMessages(prev => prev.slice(0, -1)); // Remove the user message
          sendChatMessage(retryAttempt + 1);
        }, 1000 * (retryAttempt + 1));
      } else {
        setErrors(prev => ({ 
          ...prev, 
          message: 'Failed to send message. Please try again.' 
        }));
        // Restore the message for user to retry manually
        setCurrentMessage(messageToSend);
        setMessages(prev => prev.slice(0, -1)); // Remove the user message
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  const playVoicePreview = async (voiceId, previewFile) => {
    if (playingPreview === voiceId) {
      setPlayingPreview(null);
      return;
    }

    try {
      setPlayingPreview(voiceId);
      const audio = new Audio(`https://parrotmate.com/api/voices/preview/${previewFile}`);
      audio.onended = () => setPlayingPreview(null);
      audio.onerror = () => setPlayingPreview(null);
      await audio.play();
    } catch (error) {
      console.error('Failed to play preview:', error);
      setPlayingPreview(null);
    }
  };

  const completeParrotCreation = async (retryAttempt = 0) => {
    if (!conversationId || !selectedVoice) {
      setErrors(prev => ({ 
        ...prev, 
        completion: 'Missing required data. Please ensure you have selected a voice and completed the conversation.' 
      }));
      return;
    }

    setIsCompleting(true);
    setErrors(prev => ({ ...prev, completion: null }));
    
    try {
      const token = await getToken();
      const response = await fetch('https://parrotmate.com/api/parrots/chat/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          voice_id: selectedVoice,
          temperature: temperature,
          // Enhanced completion data
          parrot_name: parrotName || 'My Parrot',
          mode: mode,
          phone_number: selectedPhoneNumber,
          creation_context: {
            steps_completed: currentStep,
            total_messages: messages.length,
            creation_time: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Enhanced success message with more details
        const successMessage = `🎉 Your parrot "${parrotName || 'My Parrot'}" has been created successfully!

First Message: "${data.first_message}"

${selectedPhoneNumber ? `Phone Number: ${selectedPhoneNumber.friendlyName}` : ''}
Voice: ${voices.find(v => v.service_voice_id === selectedVoice)?.parrotmate_name || 'Selected Voice'}
Temperature: ${temperature}

Your parrot is now ready to handle calls and conversations!`;

        setMessages(prev => [...prev, {
          role: 'system',
          content: successMessage,
          timestamp: new Date()
        }]);

        // Save completion progress if enabled
        if (enableProgressSaving) {
          saveProgress('completed', {
            parrot_id: data.agent?.id || data.parrot_id,
            completion_time: new Date().toISOString()
          });
        }

        // Call completion callback with enhanced parrot data
        if (onComplete) {
          onComplete({
            parrot: data.agent || data.parrot,
            parrot_id: data.agent?.id || data.parrot_id,
            first_message: data.first_message,
            name: parrotName || 'My Parrot',
            voice_id: selectedVoice,
            temperature: temperature,
            phone_number: selectedPhoneNumber,
            phone_numbers: selectedPhoneNumbers,
            conversation_id: conversationId,
            messages: messages
          });
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to complete parrot creation');
      }
    } catch (error) {
      console.error('Error completing configuration:', error);
      
      // Enhanced retry logic for completion
      if (retryAttempt < 2) {
        setTimeout(() => {
          completeParrotCreation(retryAttempt + 1);
        }, 2000 * (retryAttempt + 1));
      } else {
        setErrors(prev => ({ 
          ...prev, 
          completion: 'Failed to create parrot. Please try again or contact support if the issue persists.' 
        }));
      }
    } finally {
      setIsCompleting(false);
    }
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return showNameInput ? parrotName.trim().length > 0 : true;
      case 2: return showVoiceSelection ? selectedVoice : true;
      case 3: return showCompletion;
      default: return true;
    }
  };

  // Enhanced progress saving functionality
  const saveProgress = async (step, data = {}) => {
    if (!enableProgressSaving) return;
    
    try {
      const progressKey = `parrot_creation_progress_${mode}`;
      const progressData = {
        step,
        parrot_name: parrotName,
        selected_voice: selectedVoice,
        temperature,
        conversation_id: conversationId,
        messages: messages.slice(-5), // Save last 5 messages
        timestamp: new Date().toISOString(),
        ...data
      };
      
      localStorage.setItem(progressKey, JSON.stringify(progressData));
      setLastSavedStep(currentStep);
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const loadProgress = async () => {
    if (!enableProgressSaving) return null;
    
    try {
      const progressKey = `parrot_creation_progress_${mode}`;
      const savedData = localStorage.getItem(progressKey);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
    return null;
  };

  const handleNext = async () => {
    if (!canProceedToNext()) return;
    
    const maxStep = steps[steps.length - 1].id;
    if (currentStep < maxStep) {
      const nextStep = currentStep + 1;
      
      // Save progress before moving to next step
      await saveProgress(`step_${currentStep}_completed`, {
        parrot_name: parrotName,
        selected_voice: selectedVoice,
        temperature
      });
      
      setCurrentStep(nextStep);
      
      // Notify parent component of step change
      if (onStepChange) {
        onStepChange(nextStep, {
          parrot_name: parrotName,
          selected_voice: selectedVoice,
          current_step: nextStep,
          total_steps: steps.length
        });
      }
      
      // Start chat when entering the configuration step
      if (nextStep === steps[steps.length - 1].id) {
        await startChatConversation();
      }
    } else {
      await completeParrotCreation();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    const isNameStep = showNameInput && currentStep === 1;
    const isVoiceStep = showVoiceSelection && currentStep === (showNameInput ? 2 : 1);
    const isChatStep = currentStep === steps[steps.length - 1].id;

    if (isNameStep) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-lg text-gray-700 mb-6">
              Let's start by giving your AI assistant a name. We call them "Parrots" because they're great at conversation!
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What would you like to name your Parrot?
            </label>
            <input
              type="text"
              value={parrotName}
              onChange={(e) => setParrotName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-lg dark:border-gray-600"
              placeholder="e.g., Alex, Polly, Charlie..."
              maxLength={50}
            />
            <p className="text-sm text-gray-500 mt-2">
              Choose a friendly name that represents your business or personal style.
            </p>
          </div>
        </div>
      );
    }

    if (isVoiceStep) {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <p className="text-lg text-gray-700 mb-6">
              Choose the perfect voice for {parrotName || 'your Parrot'}. Click the play button to hear a preview.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
            {voices.map((voice) => (
              <div
                key={voice.service_voice_id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedVoice === voice.service_voice_id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedVoice(voice.service_voice_id)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{voice.parrotmate_name}</h3>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      voice.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                    }`}>
                      {voice.gender}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      playVoicePreview(voice.service_voice_id, voice.preview_audio_file);
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      playingPreview === voice.service_voice_id
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-teal-600 hover:bg-teal-700 text-white'
                    }`}
                    title={playingPreview === voice.service_voice_id ? 'Stop Preview' : 'Play Preview'}
                  >
                    {playingPreview === voice.service_voice_id ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-2 dark:text-gray-300 dark:text-gray-600">{voice.short_description}</p>
                <p className="text-xs text-gray-500">{voice.long_description}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (isChatStep) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className={`w-20 h-20 mx-auto ${theme === 'dark' ? 'bg-orange-900' : 'bg-orange-100'} rounded-full flex items-center justify-center mb-6`}>
              <svg className={`w-10 h-10 ${theme === 'dark' ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
              Let's have a conversation to configure {parrotName || 'your Parrot'}'s personality and behavior. Our AI will guide you through the setup process.
            </p>
          </div>

          {/* Enhanced Error Display */}
          {errors.chat && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 font-medium">{errors.chat}</span>
              </div>
              <button
                onClick={() => startChatConversation()}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm dark:text-white"
              >
                Retry Connection
              </button>
            </div>
          )}

          {errors.message && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-yellow-800 font-medium">{errors.message}</span>
              </div>
            </div>
          )}

          {isStartingChat ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Starting conversation...</p>
            </div>
          ) : (
            <>
              {/* Chat Interface */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm h-96 flex flex-col dark:bg-gray-800 dark:border-gray-700">
                {/* Messages */}
                <div className="flex-1 p-4 overflow-y-auto">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`mb-4 ${
                        message.role === 'user' ? 'text-right' : 'text-left'
                      }`}
                    >
                      <div
                        className={`inline-block max-w-xs lg:max-w-2xl px-4 py-2 rounded-lg ${
                          message.role === 'user'
                            ? 'bg-teal-600 text-white'
                            : message.role === 'system'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="text-left mb-4">
                      <div className="inline-block bg-gray-100 text-gray-900 px-4 py-2 rounded-lg dark:bg-gray-700 dark:text-white">
                        <div className="flex items-center space-x-2">
                          <div className="animate-pulse">Thinking...</div>
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-2">
                    <textarea
                      ref={inputRef}
                      value={currentMessage}
                      onChange={(e) => setCurrentMessage(e.target.value)}
                      onKeyPress={handleChatKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                      rows="2"
                      disabled={isChatLoading}
                    />
                    <button
                      onClick={sendChatMessage}
                      disabled={isChatLoading || !currentMessage.trim()}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Instruction Message */}
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg inline-block dark:bg-gray-900 dark:text-gray-300 dark:text-gray-600">
                  💡 When you're finished configuring your parrot, type <strong>"Finalize"</strong> and then click Create Parrot to complete the setup.
                </p>
              </div>

              {/* Creativity Level */}
              <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 dark:text-white">Creativity Level</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setTemperature(0.1)}
                    className={`flex-1 p-2 rounded text-sm transition ${
                      temperature === 0.1 ? 'bg-teal-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Deterministic
                  </button>
                  <button
                    onClick={() => setTemperature(0.7)}
                    className={`flex-1 p-2 rounded text-sm transition ${
                      temperature === 0.7 ? 'bg-teal-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Creative
                  </button>
                  <button
                    onClick={() => setTemperature(1.0)}
                    className={`flex-1 p-2 rounded text-sm transition ${
                      temperature === 1.0 ? 'bg-teal-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    More Creative
                  </button>
                </div>
              </div>

              {/* Completion Status */}
              {showCompletion && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Ready to Complete!</h3>
                  <p className="text-sm text-green-700 mb-4">
                    Your conversation is complete. Click "Create Parrot" to finish setup.
                  </p>
                  {selectedPhoneNumber && (
                    <p className="text-xs text-green-600 mb-2">
                      📞 Your parrot will be connected to {selectedPhoneNumber.friendlyName}
                    </p>
                  )}
                  <p className="text-xs text-green-600">
                    🎤 Voice: {voices.find(v => v.service_voice_id === selectedVoice)?.parrotmate_name || 'Selected Voice'}
                  </p>
                </div>
              )}

              {/* Enhanced Completion Error Display */}
              {errors.completion && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-800 font-medium">Creation Failed</span>
                  </div>
                  <p className="text-sm text-red-700 mb-4">{errors.completion}</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => completeParrotCreation()}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm dark:text-white"
                    >
                      Retry Creation
                    </button>
                    <button
                      onClick={() => setErrors(prev => ({ ...prev, completion: null }))}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm dark:text-white"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return null;
  };

  const currentStepData = steps.find(step => step.id === currentStep);
  const isLastStep = currentStep === steps[steps.length - 1].id;

  return (
    <div className={`max-w-4xl mx-auto ${theme === 'dark' ? 'text-white' : 'text-gray-900'} transition-colors`}>
      {/* Enhanced Progress Steps */}
      <div className="mb-8">
        {/* Progress Indicator */}
        {enableProgressSaving && lastSavedStep > 0 && (
          <div className="mb-4 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Progress saved through step {lastSavedStep}
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all flex-shrink-0 ${
                currentStep >= step.id 
                  ? 'bg-teal-600 border-teal-600 text-white shadow-lg' 
                  : 'border-gray-300 text-gray-500'
              }`}>
                {currentStep > step.id ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">{step.id}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 transition-all ${
                  currentStep > step.id ? 'bg-teal-600' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-4 text-center">
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {currentStepData?.title}
          </h2>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            {currentStepData?.description}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 dark:bg-gray-800 dark:border-gray-700">
        {renderStepContent()}
      </div>

      {/* Enhanced Navigation */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={onCancel || handleBack}
            className={`px-6 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
              theme === 'dark' 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            disabled={isLoading || isCompleting || isStartingChat}
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>
          
          {/* Progress indicator */}
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Step {currentStep} of {steps.length}
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Retry button for errors */}
          {(errors.chat || errors.message || errors.completion) && (
            <button
              onClick={() => {
                setErrors({});
                if (errors.chat) startChatConversation();
                if (errors.completion) completeParrotCreation();
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm dark:text-white"
            >
              Retry
            </button>
          )}
          
          <button
            onClick={handleNext}
            disabled={!canProceedToNext() || isLoading || isCompleting || isStartingChat}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 dark:text-white"
          >
            {isCompleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : isStartingChat ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Starting...</span>
              </>
            ) : isLastStep ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Create Parrot</span>
              </>
            ) : (
              <>
                <span>Next</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParrotCreation;
